import _ from 'lodash';
import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { statesWithNationalCategoryOptions } from 'constants/options';
import { allStatesWithNationalCategory } from 'constants/states';
import {
  convertFormsToListOptions,
  flatDynamicFields,
  flatDynamicFieldsToValueName,
  getDuplicateDynamicFieldNames,
} from 'utils/forms';
import { naturalSort } from 'utils/sort';
import { caseInsensitiveIncludes } from 'utils/string';

import { getCurrentSearchValue } from 'store/search/selectors';

import { selectUsedFormIdsToBundle } from './formBundleForms.selectors';

const getRecursiveFieldsFromSchema = (schema, parentCategory = '') => {
  return schema.reduce((acc, category) => {
    if (category.categories?.length) {
      acc.push(...getRecursiveFieldsFromSchema(category.categories, category.title));
    }

    if (category.fields?.length) {
      acc.push(
        ...category.fields.map(field => {
          return {
            group: parentCategory ? `${parentCategory} : ${category.title}` : category.title,
            name: field.title,
            value: field.value,
            type: field.type,
          };
        }),
      );
    }

    return acc;
  }, []);
};

const selectFormsState = ({ forms }) => forms;

export const selectForms = createSelector(
  selectFormsState,
  ({ normalizedItems }) => normalizedItems,
);

export const selectLoanSpecificForms = createSelector(
  selectFormsState,
  ({ loanSpecificForms }) => loanSpecificForms,
);

export const selectLoanSpecificFormsListOptions = createSelector(
  selectLoanSpecificForms,
  convertFormsToListOptions,
);

export const selectDynamicFieldsSchema = createSelector(
  selectFormsState,
  ({ dynamicFieldsSchema }) => dynamicFieldsSchema,
);

const selectTemplateVariablesSchema = createSelector(
  selectFormsState,
  ({ templateVariablesSchema }) => templateVariablesSchema,
);

export const selectCombinedDynamicFieldsSchema = createSelector(
  selectDynamicFieldsSchema,
  selectTemplateVariablesSchema,
  (dynamicFieldsSchema, templateVariablesSchema) =>
    dynamicFieldsSchema.concat(templateVariablesSchema),
);

export const selectDynamicFieldsOptions = createSelector(
  selectCombinedDynamicFieldsSchema,
  schema => getRecursiveFieldsFromSchema(schema),
);

export const selectDynamicFieldsNamesById = createSelector(
  selectDynamicFieldsSchema,
  dynamicFieldsSchema =>
    dynamicFieldsSchema ? flatDynamicFieldsToValueName(dynamicFieldsSchema) : {},
);

export const selectDynamicFieldsMap = createSelector(
  selectDynamicFieldsSchema,
  dynamicFieldsSchema => (dynamicFieldsSchema ? flatDynamicFields(dynamicFieldsSchema) : {}),
);

export const selectDuplicateDynamicFieldNames = createSelector(
  selectDynamicFieldsSchema,
  getDuplicateDynamicFieldNames,
);

export const selectFormsList = createSelector(selectFormsState, ({ normalizedItems }) =>
  Object.values(normalizedItems)
    .filter(({ isDeleted }) => !isDeleted)
    .sort((a, b) => naturalSort(a.name?.trim(), b.name?.trim())),
);

export const selectFormsListByState = memoizeOne(state =>
  createSelector(selectFormsList, forms => forms.filter(form => form.state === state)),
);

const selectFormsListOptions = createSelector(selectFormsList, convertFormsToListOptions);

export const selectIsFormsInfoLoaded = ({ forms }) => forms.formsInfo !== null;

export const selectFormsInfoVersion = ({ forms }) => forms.formsInfoVersion;

const selectFormsInfoList = createSelector(selectFormsState, ({ formsInfo }) =>
  [...(formsInfo ?? [])].sort((a, b) => naturalSort(a.name?.trim(), b.name?.trim())),
);

export const selectFormsInfoListOptions = createSelector(
  selectFormsInfoList,
  convertFormsToListOptions,
);

export const selectFormsInfoNamesById = createSelector(selectFormsInfoListOptions, options =>
  Object.fromEntries(options.map(({ value, name }) => [value, name])),
);

export const selectStateOptionsWithExistingFormsInfo = createSelector(
  selectFormsInfoList,
  forms => {
    const statesWithExistingForms = _.groupBy(forms, 'state');

    return statesWithNationalCategoryOptions.filter(({ value }) => statesWithExistingForms[value]);
  },
);

export const selectExpandedFormTreeItems = createSelector(
  selectFormsState,
  ({ expandedTreeItems }) => expandedTreeItems,
);

export const selectFilteredAreasWithNationalCategory = createSelector(
  [getCurrentSearchValue, selectFormsList],
  (searchText, formsList) => {
    if (!searchText.length) {
      return allStatesWithNationalCategory;
    }

    return allStatesWithNationalCategory.reduce((acc, [_, code]) => {
      const isAnyItemsInArea = formsList.find(
        ({ state, name }) => state === code && caseInsensitiveIncludes(name, searchText),
      );

      if (isAnyItemsInArea && !acc.find(([__, existingAreaCode]) => existingAreaCode === code)) {
        acc.push([_, code]);
      }

      return acc;
    }, []);
  },
);

export const selectFormsTreeItems = createSelector(
  [selectFilteredAreasWithNationalCategory, selectFormsList, getCurrentSearchValue],
  (filteredAreas, formsList, searchText) =>
    filteredAreas.map(([__, code]) =>
      formsList.filter(({ state, name }) => {
        const isFormInArea = state === code;

        if (!searchText.length || !isFormInArea) {
          return isFormInArea;
        }

        const isFormAvailableOnSearch = caseInsensitiveIncludes(name, searchText);

        return isFormInArea && isFormAvailableOnSearch;
      }),
    ),
);

const groupFormsOptionsByStateCategory = formsListOptions =>
  formsListOptions
    .map(form => ({
      ...form,
      categoryName: allStatesWithNationalCategory.find(([__, code]) => code === form.state)?.[0],
    }))
    .sort((a, b) => a.categoryName?.localeCompare(b.categoryName));

export const selectFormsListByGroupNameOptions = createSelector(
  [selectFormsListOptions],
  groupFormsOptionsByStateCategory,
);

export const selectFormsInfoListByGroupNameOptions = createSelector(
  [selectFormsInfoListOptions],
  groupFormsOptionsByStateCategory,
);

const selectFormsForFormBundleForm = memoizeOne(requiredState =>
  createSelector([selectFormsList, selectUsedFormIdsToBundle], (forms, usedFormIdsToBundle) =>
    forms.filter(
      ({ state, formId }) =>
        !usedFormIdsToBundle.includes(formId) && (requiredState ? state === requiredState : true),
    ),
  ),
);

export const selectFormOptionsForFormBundleForm = memoizeOne(requiredState =>
  createSelector(selectFormsForFormBundleForm(requiredState), convertFormsToListOptions),
);

export const selectStateOptionsWithExistingForms = createSelector(
  selectFormsForFormBundleForm(),
  forms => {
    const statesWithExistingForms = _.groupBy(forms, 'state');

    return statesWithNationalCategoryOptions.filter(({ value }) => statesWithExistingForms[value]);
  },
);
